/* eslint-disable */

import React, { useEffect } from "react";
import { MobXProviderContext, Observer } from "mobx-react";
import { reaction } from "mobx";
import { createMapAdapter } from "../../map";
import { getUpdateInterval } from "../../config/hereConfig";

const cor = "#1b468d";
const corSelecionada = "#669900";

// Âncoras/tamanhos dos ícones SVG (usados pelo Leaflet; o HERE auto-dimensiona).
// Âncora no CENTRO para reproduzir o padrão do HERE (H.map.Icon ancora no
// centro do bitmap por padrão, que era o comportamento original do app).
const PEDIDO_ICON = { size: [46, 46], anchor: [23, 23] };
const ORIGEM_ICON = { size: [46, 46], anchor: [23, 23] };
const ENTREGADOR_ICON = { size: [56, 60], anchor: [28, 30] };

// Metadados dos marcadores de entregadores (cor/nome/animação), por chave.
let entregadorMeta = new Map();

// Função para verificar se a data está dentro das últimas N horas
function isWithinLastHours(dateString, hours) {
  if (!dateString || dateString.length !== 14) return false;

  // Parse da string: "20251022110818" -> YYYY MM DD HH mm ss
  const year = parseInt(dateString.substr(0, 4));
  const month = parseInt(dateString.substr(4, 2)) - 1; // JS months são 0-indexed
  const day = parseInt(dateString.substr(6, 2));
  const hour = parseInt(dateString.substr(8, 2));
  const minute = parseInt(dateString.substr(10, 2));
  const second = parseInt(dateString.substr(12, 2));

  const dataGeolocalizacao = new Date(year, month, day, hour, minute, second);
  const agora = new Date();
  const diferencaMs = agora - dataGeolocalizacao;
  const diferencaHoras = diferencaMs / (1000 * 60 * 60);

  return diferencaHoras <= hours;
}

// Função para limpar animações de entregadores
function clearEntregadoresAnimations() {
  entregadorMeta.forEach((meta) => {
    if (meta._animationInterval) {
      clearInterval(meta._animationInterval);
      meta._animationInterval = null;
    }
  });
}

// Calcula a luminosidade relativa de uma cor (WCAG 2.0)
function getRelativeLuminance(hexColor) {
  // Remove o # se existir
  const hex = hexColor.replace('#', '');

  // Converte hex para RGB
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;

  // Aplica a correção gamma
  const rsRGB = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const gsRGB = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const bsRGB = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

  // Calcula a luminosidade relativa
  return 0.2126 * rsRGB + 0.7152 * gsRGB + 0.0722 * bsRGB;
}

// Retorna a cor de contraste ideal (preto ou branco) para uma cor de fundo
function getContrastColor(backgroundColor) {
  const luminance = getRelativeLuminance(backgroundColor);
  // Se a luminosidade é maior que 0.5, usa preto, senão usa branco
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

function getMarker(id, cor, outlined = false, customColor = false) {
  // Se tem cor customizada, calcula a melhor cor de contraste
  const contrastColor = customColor ? getContrastColor(cor) : '#FFFFFF';

  // Borda dupla para seleção com custom color - destaque forte e visível
  const strokeStyle = outlined ? `stroke="${contrastColor}" stroke-width="24"` : '';
  const outerStroke = outlined ? `<path d="M40.479,159.021c21.032,39.992,49.879,74.22,85.732,101.756c0.656,0.747,1.473,1.382,2.394,1.839   c0.838-0.396,1.57-0.962,2.178-1.647c80.218-61.433,95.861-125.824,96.44-128.34c2.366-9.017,3.57-18.055,3.57-26.864    C237.389,47.429,189.957,0,131.665,0C73.369,0,25.946,47.424,25.946,105.723c0,8.636,1.148,17.469,3.412,26.28" fill="none" stroke="${contrastColor === '#FFFFFF' ? '#000000' : '#FFFFFF'}" stroke-width="32"/>` : '';

  return '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" id="Capa_1" x="0px" y="0px" width="46" height="46" viewBox="0 0 263.335 263.335" style="enable-background:new 0 0 263.335 263.335;" xml:space="preserve">' +
    '<g><g xmlns="http://www.w3.org/2000/svg"> ' +
    outerStroke +
    `<path d="M40.479,159.021c21.032,39.992,49.879,74.22,85.732,101.756c0.656,0.747,1.473,1.382,2.394,1.839   c0.838-0.396,1.57-0.962,2.178-1.647c80.218-61.433,95.861-125.824,96.44-128.34c2.366-9.017,3.57-18.055,3.57-26.864    C237.389,47.429,189.957,0,131.665,0C73.369,0,25.946,47.424,25.946,105.723c0,8.636,1.148,17.469,3.412,26.28" fill="${cor}" ${strokeStyle}/>` +
    `<text x="50%" y="110" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="5em" font-weight="bold" fill="${contrastColor}">${id}</text></g></g></svg>`;
}

function getOriginSvg() {
  return (
    '<svg width="46" height="46"  viewBox="0 0 32.811 37.261" xmlns="http://www.w3.org/2000/svg" xmlns:bx="https://boxy-svg.com">' +
    '  <path d="M -16.619 -33.415 Q -16.045 -34.247 -15.472 -33.415 L -10.767 -26.589 Q -10.193 -25.757 -11.34 -25.757 L -20.751 -25.757 Q -21.898 -25.757 -21.324 -26.589 Z" style="stroke: rgb(0, 0, 0); fill: rgb(118, 118, 118);" transform="matrix(-1, 0, 0, -1, 0, 0)" bx:shape="triangle -21.898 -34.247 11.705 8.49 0.5 0.098 1@89ce0d7b"/>' +
    '  <ellipse style="stroke: rgb(0, 0, 0); fill: rgb(118, 118, 118);" cx="16.081" cy="15.191" rx="13" ry="12.36"/>' +
    '  <path style="fill: rgb(216, 216, 216); stroke: rgb(0, 0, 0);" d="M 7.875 9.758 L 8.245 8.897 L 9.771 10.784 L 10.387 10.343 L 8.849 8.475 L 9.413 8.067 L 11.115 10.061 L 11.554 9.609 L 10 7.739 L 10.815 7.634 L 12.85 10.072 L 12.903 11.521 L 23.029 22.547 L 22.633 23.42 L 21.589 23.72 L 11.769 12.315 L 10.105 12.176 L 7.875 9.758 Z"/>' +
    '  <path style="fill: rgb(216, 216, 216); stroke: rgb(0, 0, 0);" d="M 7.693 22.049 L 24.138 7.342 L 24.177 8.925 L 23.869 10.46 L 23.033 11.926 L 21.839 13.443 L 20.458 14.768 L 18.772 15.738 L 17.212 16.148 L 16.198 16.253 L 8.61 22.774 L 7.83 22.693 L 7.693 22.049 Z" bx:origin="0.512 0.505"/>' +
    '</svg>'
  );
}

function buildOriginMarker(mapAdapter, lat, lng) {
  if (mapAdapter.hasMarker('origem')) {
    const pos = mapAdapter.getMarkerPosition('origem');
    if (!pos || pos.lat !== lat || pos.lng !== lng) {
      mapAdapter.setMarkerPosition('origem', lat, lng);
    }
  } else {
    mapAdapter.addMarker('origem', lat, lng, getOriginSvg(), {
      zIndex: 150,
      ...ORIGEM_ICON,
    });
  }
}

function updateIcon(mapAdapter, pedido, id, entregadorCor = null) {
  // Check if entregador has a custom color
  const hasCustomColor = entregadorCor && entregadorCor.trim() !== '';

  let markerColor;
  let useOutline = false;

  if (hasCustomColor) {
    // Use entregador color with contrast-optimized outline for selection
    markerColor = entregadorCor;
    useOutline = pedido.selected;
  } else {
    // Use old behavior: blue when unselected, green when selected
    markerColor = pedido.selected ? corSelecionada : cor;
    useOutline = false;
  }

  mapAdapter.setMarkerIcon(
    id,
    getMarker(id, markerColor, useOutline, hasCustomColor),
    PEDIDO_ICON
  );
}

function getEntregadorMarker(cor, nome) {
  const markerColor = (cor && cor.trim() !== '') ? cor : '#d8e580';

  // Iniciais (duas letras, maiúsculas)
  const iniciais = (nome || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(n => n[0].toUpperCase())
    .join('.') || 'E';

  return (
    '<svg width="56" height="60" viewBox="0 0 220 160" xmlns="http://www.w3.org/2000/svg">' +
      '<defs>' +
        '<filter id="shadow" x="0" y="0" width="200%" height="200%">' +
          '<feDropShadow dx="0" dy="4" stdDeviation="6" flood-color="rgba(0,0,0,0.4)"/>' +
        '</filter>' +
      '</defs>' +

      // corpo e pezinho integrados em um único path
      '<path d="M8,40 a24,24 0 0 1 24,-24 h156 a24,24 0 0 1 24,24 v60 a24,24 0 0 1 -24,24 h-56 l-22,30 l-22,-30 h-56 a24,24 0 0 1 -24,-24 z" ' +
      'fill="' + markerColor + '" stroke="#000" stroke-width="6" filter="url(#shadow)" />' +

      // texto centralizado
      '<text x="110" y="80" text-anchor="middle" dominant-baseline="middle" ' +
      'font-family="Arial, sans-serif" font-weight="900" font-size="65" ' +
      'fill="#fff" stroke="#000" stroke-width="8" paint-order="stroke">' + iniciais + '</text>' +
    '</svg>'
  );
}

// Animação suave para movimentação de marcadores (via adapter)
function animateMarker(mapAdapter, key, meta, fromLat, fromLng, toLat, toLng, duration = 12000) {
  // Calcula distância do movimento (aproximada em graus)
  const distance = Math.sqrt(Math.pow(toLat - fromLat, 2) + Math.pow(toLng - fromLng, 2));

  // Se a distância for muito pequena (< 0.00001 graus ~= 1 metro), não anima
  if (distance < 0.00001) {
    mapAdapter.setMarkerPosition(key, toLat, toLng);
    return;
  }

  // Usa setInterval para animação mais consistente e menos pesada que requestAnimationFrame
  const FPS = 24; // 24 FPS - suave como cinema
  const frameDelay = 1000 / FPS;
  const startTime = Date.now();

  // Limpa intervalo anterior se existir
  if (meta._animationInterval) {
    clearInterval(meta._animationInterval);
  }

  meta._animationInterval = setInterval(() => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // Easing function suave (easeInOutQuad)
    const easeProgress = progress < 0.5
      ? 2 * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 2) / 2;

    // Interpola latitude e longitude
    const currentLat = fromLat + (toLat - fromLat) * easeProgress;
    const currentLng = fromLng + (toLng - fromLng) * easeProgress;

    mapAdapter.setMarkerPosition(key, currentLat, currentLng);

    if (progress >= 1) {
      mapAdapter.setMarkerPosition(key, toLat, toLng);
      clearInterval(meta._animationInterval);
      meta._animationInterval = null;
    }
  }, frameDelay);
}

function buildEntregadorMarker(mapaStore, entregador) {
  const entregadorKey = 'entregador_' + entregador.id;
  const mapAdapter = mapaStore.mapAdapter;

  // Verifica se o marcador já existe
  if (mapAdapter.hasMarker(entregadorKey)) {
    const meta = entregadorMeta.get(entregadorKey) || {};
    const currentPos = mapAdapter.getMarkerPosition(entregadorKey);
    const newLat = entregador.lat;
    const newLng = entregador.lng;

    // Verifica se a posição mudou -> anima
    if (currentPos && (currentPos.lat !== newLat || currentPos.lng !== newLng)) {
      animateMarker(mapAdapter, entregadorKey, meta, currentPos.lat, currentPos.lng, newLat, newLng);
    }

    // Atualiza o ícone APENAS se a cor ou nome mudou
    if (meta.cor !== entregador.cor || meta.nome !== entregador.nome) {
      mapAdapter.setMarkerIcon(
        entregadorKey,
        getEntregadorMarker(entregador.cor, entregador.nome),
        ENTREGADOR_ICON
      );
      meta.cor = entregador.cor;
      meta.nome = entregador.nome;
    }
    entregadorMeta.set(entregadorKey, meta);
    return;
  }

  // Criar novo marcador
  mapAdapter.addMarker(
    entregadorKey,
    entregador.lat,
    entregador.lng,
    getEntregadorMarker(entregador.cor, entregador.nome),
    { zIndex: 1000, ...ENTREGADOR_ICON }
  );
  entregadorMeta.set(entregadorKey, {
    cor: entregador.cor,
    nome: entregador.nome,
    _animationInterval: null,
  });
}

function buildMarker(mapaStore, id, lat, lng, selected, sequenciaPdv, entregadorCor = null) {
  const mapAdapter = mapaStore.mapAdapter;

  // Check if entregador has a custom color
  const hasCustomColor = entregadorCor && entregadorCor.trim() !== '';

  let markerColor;
  let useOutline = false;

  if (hasCustomColor) {
    markerColor = entregadorCor;
    useOutline = selected;
  } else {
    markerColor = selected ? corSelecionada : cor;
    useOutline = false;
  }

  mapAdapter.addMarker(
    id,
    lat,
    lng,
    getMarker(sequenciaPdv, markerColor, useOutline, hasCustomColor),
    {
      zIndex: 100,
      ...PEDIDO_ICON,
      onTap: () => {
        var indexMarker = mapaStore.pedidosMapa.map(elem => elem.id).indexOf(id);
        if (indexMarker > -1) {
          mapaStore.toggleSelected(mapaStore.pedidosMapa[indexMarker]);
          updateIcon(mapAdapter, mapaStore.pedidosMapa[indexMarker], sequenciaPdv, entregadorCor);
        }
      },
    }
  );

  // Calcula distância do pedido até a origem (uma vez), conforme a aba atual.
  const origem = { lat: mapaStore.origemRestaurante.lat, lng: mapaStore.origemRestaurante.lng };
  const listas = [
    mapaStore.pedidosPendentes,
    mapaStore.pedidosACaminho,
    mapaStore.pedidosFinalizados,
    mapaStore.pedidosCancelados,
  ];
  // Modo flexível tem a aba Rotas (índices diferentes): usa o grupo da aba.
  const lista = mapaStore.distribuicaoFlexivel
    ? mapaStore._grupoDaAbaFlex()
    : (listas[mapaStore.tabIndex] || mapaStore.pedidosPendentes);
  const index = lista.map(elem => elem.id).indexOf(id);
  if (index > -1 && lista[index]['distancia'] == undefined) {
    const km = mapAdapter.distanceMeters({ lat, lng }, origem);
    lista[index]['distancia'] = (km / 1000).toFixed(1) + " km";
  }
}

function Mapa(props) {
  const { mapaStore } = React.useContext(MobXProviderContext);
  let ref = React.createRef();

  useEffect(() => {
    mapaStore.rebuildStore();

    if (!mapaStore.mapAdapter) {
      const adapter = createMapAdapter();
      adapter.init({
        container: ref.current,
        center: {
          lat: mapaStore.origemRestaurante.lat - 0.005,
          lng: mapaStore.origemRestaurante.lng - 0.035,
        },
        zoom: props.zoom,
        onViewChange: (zoom, lat, lng) => {
          const { onMapViewChange } = props;
          if (onMapViewChange) onMapViewChange(zoom, lat, lng);
        },
      });

      mapaStore.setMapAdapter(adapter);
    }

    mapaStore.buscarPedidos();
  }, []);

  useEffect(() => {
    // Usa intervalo dinâmico baseado no horário
    const updateInterval = getUpdateInterval();
    let lastUpdateTime = Date.now();

    const interval = setInterval(() => {
      // Só atualiza se a aba está ativa/visível
      if (!document.hidden) {
        mapaStore.buscarPedidos();
        lastUpdateTime = Date.now();
      }
    }, updateInterval);

    // Listener para quando a aba muda de visibilidade
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const timeSinceLastUpdate = Date.now() - lastUpdateTime;
        if (timeSinceLastUpdate > 5000) {
          console.log(`Aba voltou a ficar ativa após ${Math.round(timeSinceLastUpdate / 1000)}s - atualizando pedidos`);
          mapaStore.buscarPedidos();
          lastUpdateTime = Date.now();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(
    () =>
      reaction(
        () => mapaStore.updateMap,
        updateMap => {
          if (updateMap) {
            const mapAdapter = mapaStore.mapAdapter;

            // Se o originMarker precisa ser resetado (ex: mudança de tab)
            if (mapaStore.originMarkerNeedsReset) {
              mapaStore.originMarkerNeedsReset = false;
              // Limpa animações ao trocar de tab para economizar recursos
              clearEntregadoresAnimations();
            }

            // Limpar marcadores de pedidos e origem (preserva entregadores)
            mapAdapter.removeMarkersWhere(
              (key) => !String(key).startsWith('entregador_')
            );

            // Adicionar marcador de origem
            buildOriginMarker(mapAdapter, mapaStore.origemRestaurante.lat, mapaStore.origemRestaurante.lng);

            // Adicionar marcadores dos pedidos
            for (let i = 0; i < mapaStore.pedidosMapa.length; i++) {
              let entregadorCor = null;
              const pedido = mapaStore.pedidosMapa[i];

              if (pedido.entregador && pedido.entregador.EntregadorId) {
                const entregador = mapaStore.entregadores.find(e => e.id === pedido.entregador.EntregadorId);
                if (entregador && entregador.cor) {
                  entregadorCor = entregador.cor;
                }
              }

              buildMarker(mapaStore, pedido.id, pedido.lat, pedido.lng, pedido.selected, pedido.sequenciaPdv, entregadorCor);
            }

            // OTIMIZAÇÃO: Só processa entregadores se existirem
            if (mapaStore.entregadores && mapaStore.entregadores.length > 0) {
              // Conjunto de entregadores que devem aparecer
              const activeEntregadorIds = new Set();
              for (let i = 0; i < mapaStore.entregadores.length; i++) {
                const entregador = mapaStore.entregadores[i];
                if (entregador.ativo === 1 &&
                    entregador.lat &&
                    entregador.lng &&
                    isWithinLastHours(entregador.dataUltimaGeoLocalizacao, 2)) {
                  activeEntregadorIds.add('entregador_' + entregador.id);
                }
              }

              // Remover marcadores de entregadores que não estão mais ativos
              mapAdapter.getMarkerKeys()
                .filter((key) => String(key).startsWith('entregador_') && !activeEntregadorIds.has(key))
                .forEach((key) => {
                  mapAdapter.removeMarker(key);
                  const meta = entregadorMeta.get(key);
                  if (meta && meta._animationInterval) {
                    clearInterval(meta._animationInterval);
                  }
                  entregadorMeta.delete(key);
                });

              // Adicionar/atualizar marcadores dos entregadores
              for (let i = 0; i < mapaStore.entregadores.length; i++) {
                const entregador = mapaStore.entregadores[i];
                if (entregador.ativo === 1 &&
                    entregador.lat &&
                    entregador.lng &&
                    isWithinLastHours(entregador.dataUltimaGeoLocalizacao, 2)) {
                  buildEntregadorMarker(mapaStore, entregador);
                }
              }
            }

            mapaStore.updateMap = false;
          }
        }
      ),
    [],
  )

  useEffect(() => {
    // componentWillUnmount - Limpeza de memória
    return () => {
      if (mapaStore.mapAdapter) {
        mapaStore.mapAdapter.destroy();
      }

      // Limpa todas as animações de entregadores ao desmontar
      clearEntregadoresAnimations();
      entregadorMeta.clear();
    }
  }, []);

  var height = window.innerHeight

  return (
    <div>
      <Observer >{() =>
        <div
          id='mapContainer'
          style={{ position: 'relative', width: '100%', height: `${height - 70}px` }}
          ref={ref}
        />
      }</Observer>
    </div>
  )
}

export default Mapa;
